import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useContext, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import StarOutlineRoundedIcon from '@mui/icons-material/StarOutlineRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import { useTranslation } from 'react-i18next';
import { ActivityContext } from '@/contexts/ActivityContext';
import { ActivityFormContext } from '@/contexts/ActivityFormContext';
import { useStarValuePersona } from '@/personas/useStarValuePersona';
import { starsToXp } from '@/utils/xp';

/** Aktiviteten kan vara värd 1-5 stjärnor. Samma spann som backend validerar. */
export const STAR_VALUE_OPTIONS = [1, 2, 3, 4, 5];

/**
 * Hela spannet 1-5 kräver godkännande enligt kontots serverpolicy - inget värde
 * undantas, annars kunde brukaren fastställa ett värde själv genom att välja 1.
 * Tonåringsvyn visar XP; visningspersonan ändrar inte godkännandekravet.
 * Backend sätter auktoritativt status när aktiviteten skapas.
 */
const StarValueForm = () => {
  const { activity, setActivity } = useContext(ActivityContext);
  const formHandler = useContext(ActivityFormContext);
  const { t } = useTranslation();
  const theme = useTheme();
  const { asXp, needsApproval } = useStarValuePersona();

  const selected = activity.starValue;
  const FilledIcon = asXp ? BoltRoundedIcon : StarRoundedIcon;
  const EmptyIcon = asXp ? BoltOutlinedIcon : StarOutlineRoundedIcon;

  useEffect(() => {
    // Steget är klart först när ett värde faktiskt är valt.
    formHandler.setValid(selected !== undefined);
  }, [selected]);

  const optionLabel = (starValue: number) =>
    asXp ? t('starValue.xpAmount', { xp: starsToXp(starValue) }) : `${starValue}`;

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1" textAlign="center">
        {asXp ? t('starValue.questionXp') : t('starValue.question')}
      </Typography>

      <Stack spacing={1}>
        {STAR_VALUE_OPTIONS.map((starValue) => {
          const isSelected = selected === starValue;
          return (
            <Button
              key={starValue}
              onClick={() => setActivity({ ...activity, starValue })}
              aria-pressed={isSelected}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                outline: isSelected ? `3px solid ${theme.palette.primary.main}` : 'none',
                bgcolor: 'background.paper',
              }}
            >
              <Box sx={{ display: 'flex', flex: 1, alignItems: 'center' }}>
                {STAR_VALUE_OPTIONS.map((position) => {
                  const Icon = position <= starValue ? FilledIcon : EmptyIcon;
                  return (
                    <Icon
                      key={position}
                      sx={{
                        color:
                          position <= starValue
                            ? theme.palette.warning.main
                            : theme.palette.action.disabled,
                        fontSize: '2rem',
                      }}
                    />
                  );
                })}
                <Typography variant="subtitle1" sx={{ ml: 1.5, fontWeight: 600 }}>
                  {optionLabel(starValue)}
                </Typography>
              </Box>
            </Button>
          );
        })}
      </Stack>

      {needsApproval && selected !== undefined && (
        <Alert severity="info">{t('starValue.approvalNotice')}</Alert>
      )}
    </Stack>
  );
};

export default StarValueForm;
